import { Component, ElementRef, inject, OnInit, OnDestroy, ViewChild, NgZone } from '@angular/core';
import {
  ISidebarLink,
  SidebarComponent,
  SidebarNextSend,
} from '../../../components/ui/navigation/sidebar/sidebar.component';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { TopNavbarComponent } from '../../../components/ui/navigation/top-navbar/top-navbar.component';
import { SettingsService } from '../../../services/settings/settings.service';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { MessageService } from '../../../services/message/message.service';
import { MessageTemplate } from '../../../services/message-templates/message.template';
import { filter, Subscription } from 'rxjs';
import { CommandPaletteComponent } from '../../../components/ui/command-palette/command-palette.component';
import { ShortcutsSheetComponent } from '../../../components/ui/command-palette/shortcuts-sheet.component';
import {
  CommandPaletteService,
  isApplePlatform,
  isTypingTarget,
} from '../../../components/ui/command-palette/command-palette.service';
import { isScheduled, sendMoment } from '../../../../utils/sms';

/** How far ahead the Messages count looks, and how stale its data may get. */
const DUE_SOON_MS = 60 * 60 * 1000;
const UPCOMING_REFRESH_MS = 5 * 60 * 1000;

import { canViewApiTraffic } from '../../../services/routing/report-access.guard';
@Component({
  selector: 'app-portal',
  standalone: true,
  imports: [
    SidebarComponent,
    TopNavbarComponent,
    RouterOutlet,
    CommandPaletteComponent,
    ShortcutsSheetComponent,
  ],
  templateUrl: './portal.component.html',
  styleUrl: './portal.component.css',
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
  },
})
export class PortalComponent implements OnInit, OnDestroy {
  private ngZone = inject(NgZone);
  settingService = inject(SettingsService);
  contactsService = inject(ContactsService);
  messageService = inject(MessageService);
  messageTemplate = inject(MessageTemplate);
  readonly palette = inject(CommandPaletteService);
  private router = inject(Router);
  @ViewChild('view') private view?: ElementRef<HTMLElement>;
  @ViewChild('rail') private rail?: ElementRef<HTMLElement>;
  isSidebarOpen = false;
  /** Path of the page on screen, so query-only changes (tabs) keep the scroll. */
  private currentPath = '';
  isProfileOptionsOpen = false;
  private routerSubscription?: Subscription;
  private rolesSubscription?: Subscription;
  private upcomingSubscription?: Subscription;

  /** Scheduled sends due within the hour, shown as a count beside Messages. */
  private dueSoonCount = 0;
  /** The soonest scheduled send, counted down at the foot of the rail. */
  nextSend: SidebarNextSend | null = null;
  private nextSendMessage: Record<string, unknown> | null = null;
  private canViewApiReports = false;
  private upcoming: unknown[] = [];
  private upcomingFetchedAt = 0;
  private recountTimer?: ReturnType<typeof setInterval>;
  private isApple = false;

  sidebarLinks: ISidebarLink[] = this.buildSidebarLinks(false);

  private buildSidebarLinks(canViewApiReports: boolean): ISidebarLink[] {
    return [
      {
        iconName: 'sms',
        title: 'SMS',
        // Icon names are Material Symbols (Rounded), rendered by the sidebar.
        children: [
          { iconName: 'space_dashboard', title: 'Dashboard', link: '/organization/dashboard' },
          { iconName: 'group', title: 'Contacts', link: '/organization/contacts' },
          {
            iconName: 'chat_bubble',
            title: 'Messages',
            link: '/organization/messages',
            badge: this.dueSoonCount,
            badgeLabel:
              this.dueSoonCount === 1
                ? '1 scheduled send in the next hour'
                : `${this.dueSoonCount} scheduled sends in the next hour`,
          },
          { iconName: 'campaign', title: 'Campaigns', link: '/organization/campaigns' },
          { iconName: 'table_view', title: 'Custom SMS', link: '/organization/smart-broadcast' },
          { iconName: 'bar_chart', title: 'Delivery reports', link: '/organization/reports' },
          ...(canViewApiReports
            ? [{ iconName: 'query_stats', title: 'API traffic', link: '/organization/smsc-reports' } as ISidebarLink]
            : []),
        ],
      },
      {
        iconName: 'dialpad',
        title: 'USSD',
        children: [
          { iconName: 'widgets', title: 'Applications', link: '/organization/ussd-simulator/applications' },
          { iconName: 'smartphone', title: 'Simulator', link: '/organization/ussd-simulator/simulator' },
          { iconName: 'bar_chart', title: 'Reports', link: '/organization/ussd-simulator/reports' },
        ],
      },
      {
        iconName: 'payments',
        title: 'PayPoint',
        children: [
          { iconName: 'receipt_long', title: 'Bill Payment Reports', link: '/organization/paypoint-reports' },
        ],
      },
    ];
  }

  ngOnInit(): void {
    // this.router.navigate(['organization','dashboard']);
    this.settingService.fetchTestContacts();

    // Fetch all counts upfront so they're available on any page
    this.contactsService.fetchDashboardStats();
    this.contactsService.fetchDashboardSummaryCampaigns();
    this.contactsService.fetchDashboardSummaryMessages();
    this.contactsService.fetchContactsCount();
    this.contactsService.fetchOrganizationCount();
    this.contactsService.fetchTagsCount();
    this.contactsService.fetchSegmentsCount();
    this.messageService.prefetchAllCounts();
    this.messageTemplate.fetchTemplates();

    // Show the "API traffic" link only for permitted roles
    this.rolesSubscription = this.settingService.getUserRoles.subscribe((roles) => {
      // Same role list as the route guard, so link and page cannot drift.
      this.canViewApiReports = canViewApiTraffic(roles);
      this.sidebarLinks = this.buildSidebarLinks(this.canViewApiReports);
    });

    // Close sidebar on navigation (mobile/tablet only)
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.closeSidebar();
        // Navigation is the only refresh trigger: no background polling.
        this.refreshUpcoming();
        this.resetView(event.urlAfterRedirects);
      });

    if (typeof window !== 'undefined') {
      this.isApple = isApplePlatform();
      this.refreshUpcoming(true);
      // The hour window moves with the clock; recount without a request.
      // Outside the zone, so a pending timer never keeps the app from settling.
      this.recountTimer = this.ngZone.runOutsideAngular(() =>
        setInterval(() => this.ngZone.run(() => this.recountDueSoon()), 60_000)
      );
    }
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.rolesSubscription?.unsubscribe();
    this.upcomingSubscription?.unsubscribe();
    if (this.recountTimer) clearInterval(this.recountTimer);
    this.palette.closePalette();
    this.palette.closeShortcuts();
    // Ensure body scroll is restored
    document.body.style.overflow = '';
  }

  toggleSidebar() {
    if (this.isSidebarOpen) {
      this.closeSidebar(true);
      return;
    }
    this.isSidebarOpen = true;
    // Prevent body scroll on mobile when sidebar is open
    if (window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
      // Into the drawer once it is visible: the current page's link, else the first stop.
      setTimeout(() => {
        const rail = this.rail?.nativeElement;
        const target =
          rail?.querySelector<HTMLElement>('[aria-current="page"]') ??
          rail?.querySelector<HTMLElement>('a[href], button:not([disabled])');
        target?.focus();
      });
    }
  }

  /** `returnFocus` sends focus back to the menu button (Escape, scrim click). */
  closeSidebar(returnFocus = false) {
    const wasOpen = this.isSidebarOpen;
    this.isSidebarOpen = false;
    // Restore body scroll
    if (typeof document === 'undefined') return;
    document.body.style.overflow = '';
    if (wasOpen && returnFocus) {
      document.getElementById('portal-menu-button')?.focus();
    }
  }

  /** The skip link moves focus without touching the URL, which the router owns. */
  skipToContent(event: Event) {
    event.preventDefault();
    this.view?.nativeElement.focus();
  }

  /**
   * A new page starts at its top, read from the start: the routed views
   * scroll inside .portal__view, not the window. Focus moves there when it
   * was left in the rail or top bar (or on a removed element), so screen
   * readers announce the change; focus inside the page stays put.
   */
  private resetView(url: string) {
    const path = url.split(/[?#]/)[0];
    const previous = this.currentPath;
    this.currentPath = path;
    if (!previous || previous === path || typeof document === 'undefined') return;

    const view = this.view?.nativeElement;
    if (!view) return;
    view.scrollTop = 0;
    const active = document.activeElement;
    if (!active || active === document.body || !view.contains(active)) {
      view.focus({ preventScroll: true });
    }
  }

  handleProfileOptionsChange(isOpen: boolean) {
    this.isProfileOptionsOpen = isOpen;
  }

  /** The rail's countdown opens the message it counts down to. */
  openNextSend() {
    const message = this.nextSendMessage;
    const uuid = message?.['uuid'];
    if (!message || typeof uuid !== 'string') return;
    this.messageService.updateSelectedMessage(message);
    this.router.navigate(['/organization/messages', uuid]);
  }

  /** ⌘K / Ctrl+K toggles the command palette; "?" opens the shortcuts sheet. */
  onDocumentKeydown(event: KeyboardEvent) {
    if (event.defaultPrevented || event.isComposing) return;
    const key = event.key ?? '';

    const modifier = this.isApple ? event.metaKey : event.ctrlKey || event.metaKey;
    if (modifier && !event.altKey && !event.shiftKey && key.toLowerCase() === 'k') {
      event.preventDefault();
      this.palette.togglePalette();
      return;
    }

    if (key === 'Escape') {
      if (this.palette.paletteOpen()) this.palette.closePalette();
      else if (this.palette.shortcutsOpen()) this.palette.closeShortcuts();
      else if (this.isSidebarOpen) this.closeSidebar(true);
      return;
    }

    if (
      key === '?' &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey &&
      !this.palette.paletteOpen() &&
      !isTypingTarget(event.target)
    ) {
      event.preventDefault();
      if (this.palette.shortcutsOpen()) this.palette.closeShortcuts();
      else this.palette.openShortcuts();
    }
  }

  /** One small list request, at most every few minutes. */
  private refreshUpcoming(force = false) {
    if (typeof window === 'undefined') return;
    const now = Date.now();
    if (!force && now - this.upcomingFetchedAt < UPCOMING_REFRESH_MS) return;
    this.upcomingFetchedAt = now;

    this.upcomingSubscription?.unsubscribe();
    this.upcomingSubscription = this.messageService.fetchUpcomingMessages(50).subscribe({
      next: (messages) => {
        this.upcoming = (Array.isArray(messages) ? messages : []).filter((message: any) =>
          /^pending$/i.test(message?.status ?? '')
        );
        this.recountDueSoon();
      },
      // The count is a nicety; without it the link simply shows no number.
      error: () => {},
    });
  }

  private recountDueSoon() {
    const now = Date.now();
    // A send a few minutes late is still about to go; older ones are stuck.
    const live = this.upcoming
      .filter((message: any) => isScheduled(message))
      .map((message: any) => ({ message, at: sendMoment(message)?.getTime() }))
      .filter((entry): entry is { message: any; at: number } => entry.at !== undefined && entry.at >= now - 5 * 60_000)
      .sort((a, b) => a.at - b.at);

    const soonest = live[0];
    this.nextSendMessage = soonest?.message ?? null;
    this.nextSend = soonest
      ? { subject: soonest.message.messageSubject || 'Untitled message', at: new Date(soonest.at) }
      : null;

    const count = live.filter((entry) => entry.at <= now + DUE_SOON_MS).length;
    if (count === this.dueSoonCount) return;
    this.dueSoonCount = count;
    this.sidebarLinks = this.buildSidebarLinks(this.canViewApiReports);
  }
}
